import React from 'react';
import { company, websiteURL, supportEmail, socialURLs } from '../constants';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaGithub, FaTwitter, FaLinkedin, FaFacebook, FaEnvelope } from 'react-icons/fa';
import { isInsideRoleShell } from '../shared/ui/roleShell';
import { useOrganization } from '../shared/branding/OrganizationContext';
import OrgBrandLogo from '../shared/branding/OrgBrandLogo';
import { openCookieSettings } from '../utils/cookieConsent';

const Footers = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { branding, organization } = useOrganization();
  const brandName = branding?.companyName || organization?.name || company;
  const isTenantHost = Boolean(organization?.tenant);

  if (isInsideRoleShell()) {
    return null;
  }

  const cookieSettingsItem = {
    name: t('footer.legal.cookieSettings', 'Cookie settings'),
    action: 'cookie-settings',
  };

  const footerLinks = isTenantHost
    ? {
        product: [
          { name: t('footer.support.help', 'Help'), path: '/support' },
          { name: t('footer.support.status', 'Status'), path: '/status' },
        ],
        company: [
          { name: t('footer.company.contact', 'Contact'), path: '/contact' },
        ],
        legal: [
          { name: t('footer.legal.privacy', 'Privacy Policy'), path: '/privacy' },
          { name: t('footer.legal.terms', 'Terms of Service'), path: '/terms' },
          { name: t('footer.legal.cookies', 'Cookie Policy'), path: '/cookies' },
          cookieSettingsItem,
          { name: t('footer.legal.gdpr', 'GDPR'), path: '/gdpr' },
          { name: t('footer.legal.aiTransparency', 'AI transparency'), path: '/ai-transparency' },
        ],
        support: [
          { name: t('footer.support.help', 'Help Center'), path: '/help' },
          { name: t('footer.support.status', 'Status'), path: '/status' },
        ],
      }
    : {
        product: [
          { name: t('footer.product.features', 'Features'), path: '/features' },
          { name: t('footer.product.platform', 'Platform'), path: '/platform' },
          { name: t('footer.product.pricing', 'Pricing'), path: '/pricing' },
          { name: t('footer.product.courseIdeas', 'Course Ideas'), path: '/course-ideas' },
          { name: t('footer.product.desktopApp', 'Desktop App'), path: '/desktop' },
        ],
        company: [
          { name: t('footer.company.about', 'About'), path: '/about' },
          { name: t('footer.company.blog', 'Blog'), path: '/blog' },
          { name: t('footer.company.careers', 'Careers'), path: '/careers' },
          { name: t('footer.company.contact', 'Contact'), path: '/contact' },
        ],
        legal: [
          { name: t('footer.legal.legalNotice', 'Legal Notice'), path: '/legal' },
          { name: t('footer.legal.privacy', 'Privacy Policy'), path: '/privacy' },
          { name: t('footer.legal.terms', 'Terms of Service'), path: '/terms' },
          { name: t('footer.legal.cookies', 'Cookie Policy'), path: '/cookies' },
          cookieSettingsItem,
          { name: t('footer.legal.gdpr', 'GDPR'), path: '/gdpr' },
          { name: t('footer.legal.aiTransparency', 'AI transparency'), path: '/ai-transparency' },
        ],
        support: [
          { name: t('footer.support.help', 'Help Center'), path: '/help' },
          { name: t('footer.support.documentation', 'Documentation'), path: '/docs' },
          { name: t('footer.support.community', 'Community'), path: '/community' },
          { name: t('footer.support.status', 'Status'), path: '/status' },
        ],
      };

  const navigateTo = (link) => {
    if (link?.action === 'cookie-settings') {
      openCookieSettings();
      return;
    }
    if (!link?.path) return;
    navigate(link.path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const socialLinks = (isTenantHost
    ? [
        { icon: <FaEnvelope className="w-5 h-5" />, href: branding?.supportEmail ? `mailto:${branding.supportEmail}` : null, label: 'Email' },
      ]
    : [
        { icon: <FaLinkedin className="w-5 h-5" />, href: socialURLs.linkedin, label: 'LinkedIn' },
        { icon: <FaFacebook className="w-5 h-5" />, href: socialURLs.facebook, label: 'Facebook' },
        { icon: <FaTwitter className="w-5 h-5" />, href: socialURLs.twitter, label: 'Twitter' },
        { icon: <FaGithub className="w-5 h-5" />, href: socialURLs.github, label: 'GitHub' },
        { icon: <FaEnvelope className="w-5 h-5" />, href: `mailto:${supportEmail}`, label: 'Email' },
      ]
  ).filter((link) => Boolean(link.href));

  const currentYear = new Date().getFullYear();

  return (
    <footer className="w-full bg-white dark:bg-gray-950 border-t border-gray-200 dark:border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-16 md:py-20">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
            {/* Brand Section */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-6"
              >
                <a href={isTenantHost ? '/' : websiteURL} className="inline-flex items-center mb-4">
                  <OrgBrandLogo className="h-10 w-auto object-contain" alt={`${brandName} Logo`} />
                </a>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                  {isTenantHost
                    ? t('footer.descriptionTenant', 'Your organization learning workspace — courses, practice, and certificates.')
                    : t('footer.description', 'Create engaging courses with AI. Transform any topic into interactive, step-by-step learning experiences.')}
                </p>
                {/* Social Links */}
                <div className="flex items-center gap-3">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-200"
                      aria-label={social.label}
                    >
                      {social.icon}
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Product Links */}
            <div>
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4"
              >
                {t('footer.sections.product', 'Product')}
              </motion.h3>
              <ul className="space-y-3">
                {footerLinks.product.map((link, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 + index * 0.05 }}
                  >
                    <button
                      onClick={() => navigateTo(link)}
                      className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200 text-start"
                    >
                      {link.name}
                    </button>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Company Links */}
            <div>
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4"
              >
                {t('footer.sections.company', 'Company')}
              </motion.h3>
              <ul className="space-y-3">
                {footerLinks.company.map((link, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 + index * 0.05 }}
                  >
                    <button
                      onClick={() => navigateTo(link)}
                      className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200 text-start"
                    >
                      {link.name}
                    </button>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4"
              >
                {t('footer.sections.legal', 'Legal')}
              </motion.h3>
              <ul className="space-y-3">
                {footerLinks.legal.map((link, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.05 }}
                  >
                    <button
                      onClick={() => navigateTo(link)}
                      className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200 text-start"
                    >
                      {link.name}
                    </button>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Support Links */}
            <div>
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4"
              >
                {t('footer.sections.support', 'Support')}
              </motion.h3>
              <ul className="space-y-3">
                {footerLinks.support.map((link, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 + index * 0.05 }}
                  >
                    <button
                      onClick={() => navigateTo(link)}
                      className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200 text-start"
                    >
                      {link.name}
                    </button>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>

          {/* Bottom Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="pt-8 border-t border-gray-200 dark:border-gray-800"
          >
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              {/* Copyright */}
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <p>
                  © {currentYear} {company}. {t('footer.copyright.rights', 'All rights reserved.')}
                </p>
              </div>

              {/* Additional Info */}
              <div className="flex flex-wrap items-center gap-6 text-xs text-gray-500 dark:text-gray-500">
                <span>{t('footer.madeWith', 'Made with')} ❤️ {t('footer.forLearners', 'for learners worldwide')}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </footer>
  );
};

export default Footers;
